import type { IntakePacket, IntakeAnswer, IntakeGoalDraft, IntakeTrackerDraft, IntakeSection } from '@/types/intake';

const KEY_PACKETS = 'coachd:intake:packets';
const KEY_ANSWERS = 'coachd:intake:answers';
const KEY_GOALS = 'coachd:intake:goals';
const KEY_TRACKERS = 'coachd:intake:trackers';

function nowISO(): string {
  return new Date().toISOString();
}

function uid(prefix: string): string {
  return globalThis.crypto?.randomUUID?.() || `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function read<T>(key: string): T[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [] as T[];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? (arr as T[]) : [];
  } catch {
    return [] as T[];
  }
}

function write<T>(key: string, value: T[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // OBS-11 a-ignore: localStorage throws in private mode / when full; this is a best-effort cache and the app works without it.
  }
}

function foldAnswersBySection(answers: any[] | undefined) {
  const out: Record<string, Record<string, any>> = {};
  for (const a of answers || []) {
    const sec = a.section || 'misc';
    if (!out[sec]) out[sec] = {};
    out[sec][a.key] = a.value;
  }
  return out;
}

export async function createPacket(inviteId: string, coachId: string, clientEmail: string): Promise<IntakePacket> {
  const packets = read<IntakePacket>(KEY_PACKETS);
  const id = uid('pkt');
  const now = nowISO();
  const pkt: IntakePacket = {
    id,
    inviteId,
    coachId,
    clientEmail: String(clientEmail).toLowerCase(),
    status: 'draft',
    createdAt: now,
    updatedAt: now,
  };
  packets.push(pkt);
  write(KEY_PACKETS, packets);
  return pkt;
}

export async function getPacketByInvite(inviteId: string): Promise<IntakePacket | null> {
  const packets = read<IntakePacket>(KEY_PACKETS);
  const packet = packets.find((p) => String(p.inviteId) === String(inviteId)) || null;
  if (!packet) return null;
  const answers = read<IntakeAnswer>(KEY_ANSWERS).filter((a) => a.packetId === packet.id);
  const bySection = foldAnswersBySection(answers);
  return { ...(packet as any), formFlags: bySection.form_flags || (packet as any).formFlags } as IntakePacket;
}

export async function getPacketById(id: string): Promise<IntakePacket | null> {
  const packets = read<IntakePacket>(KEY_PACKETS);
  const packet = packets.find((p) => String(p.id) === String(id)) || null;
  if (!packet) return null;
  const answers = read<IntakeAnswer>(KEY_ANSWERS).filter((a) => a.packetId === packet.id);
  const bySection = foldAnswersBySection(answers);
  return { ...(packet as any), formFlags: bySection.form_flags || (packet as any).formFlags } as IntakePacket;
}

export async function upsertAnswer(
  packetId: string,
  section: IntakeSection,
  key: string,
  value: unknown,
): Promise<IntakeAnswer> {
  const answers = read<any>(KEY_ANSWERS);
  const existingIdx = answers.findIndex((a: any) => a.packetId === packetId && a.section === section && a.key === key);
  const now = nowISO();
  if (existingIdx >= 0) {
    answers[existingIdx] = { ...answers[existingIdx], value, createdAt: now };
    write(KEY_ANSWERS, answers);
    return answers[existingIdx] as IntakeAnswer;
  }
  const ans: any = { id: uid('ans'), packetId, section, key, value, createdAt: now };
  answers.push(ans);
  write(KEY_ANSWERS, answers);
  return ans as IntakeAnswer;
}

export async function addGoalDraft(
  packetId: string,
  goal: Omit<IntakeGoalDraft, 'id' | 'packetId' | 'createdAt'>,
): Promise<IntakeGoalDraft> {
  const goals = read<IntakeGoalDraft>(KEY_GOALS);
  const g: IntakeGoalDraft = { id: uid('goal'), packetId, createdAt: nowISO(), ...goal } as IntakeGoalDraft;
  goals.push(g);
  write(KEY_GOALS, goals);
  return g;
}

export async function removeGoalDraft(id: string): Promise<boolean> {
  const goals = read<IntakeGoalDraft>(KEY_GOALS);
  const next = goals.filter((g) => g.id !== id);
  write(KEY_GOALS, next);
  return next.length !== goals.length;
}

export async function listGoalsForPacket(packetId: string): Promise<IntakeGoalDraft[]> {
  const goals = read<IntakeGoalDraft>(KEY_GOALS);
  return goals.filter((g) => String(g.packetId) === String(packetId));
}

export async function addTrackerDraft(
  packetId: string,
  tracker: Omit<IntakeTrackerDraft, 'id' | 'packetId' | 'createdAt'>,
): Promise<IntakeTrackerDraft> {
  const trackers = read<IntakeTrackerDraft>(KEY_TRACKERS);
  const t: IntakeTrackerDraft = { id: uid('trk'), packetId, createdAt: nowISO(), ...tracker } as IntakeTrackerDraft;
  trackers.push(t);
  write(KEY_TRACKERS, trackers);
  return t;
}

export async function removeTrackerDraft(id: string): Promise<boolean> {
  const trackers = read<IntakeTrackerDraft>(KEY_TRACKERS);
  const next = trackers.filter((t) => t.id !== id);
  write(KEY_TRACKERS, next);
  return next.length !== trackers.length;
}

export async function listTrackersForPacket(packetId: string): Promise<IntakeTrackerDraft[]> {
  const trackers = read<IntakeTrackerDraft>(KEY_TRACKERS);
  return trackers.filter((t) => String(t.packetId) === String(packetId));
}

export async function listTrackerDrafts(packetId: string): Promise<any[]> {
  const trackers = read<any>(KEY_TRACKERS);
  const rows = (trackers || []).filter((t: any) => String(t.packetId) === String(packetId));
  return rows.map((t: any) => ({
    id: t.id,
    packetId: t.packetId,
    title: t.title || '',
    unit: t.unit || '',
    cadence: t.cadence || 'weekly',
    days: Array.isArray(t.days) ? t.days : [],
    reminders: !!t.reminders,
    showInAnalytics: !!t.showInAnalytics,
    createdAt: t.createdAt || nowISO(),
  }));
}

export async function upsertTrackerDraft(packetId: string, d: any): Promise<any> {
  const all = read<any>(KEY_TRACKERS);
  const titleLc = String(d?.title || '')
    .trim()
    .toLowerCase();
  if (!titleLc) throw new Error('title required');
  const idx = all.findIndex(
    (t: any) => String(t.packetId) === String(packetId) && String(t.title || '').toLowerCase() === titleLc,
  );
  const now = nowISO();
  const row: any = {
    id: idx >= 0 ? all[idx].id : uid('trk'),
    packetId: String(packetId),
    title: d?.title || '',
    unit: d?.unit || '',
    cadence: d?.cadence || 'weekly',
    days: Array.isArray(d?.days) ? d.days : [],
    reminders: !!d?.reminders,
    showInAnalytics: !!d?.showInAnalytics,
    createdAt: idx >= 0 ? all[idx].createdAt : now,
  };
  if (idx >= 0) all[idx] = row;
  else all.push(row);
  write(KEY_TRACKERS, all);
  return row;
}

export async function deleteTrackerDraft(packetId: string, idOrTitle: string): Promise<boolean> {
  const all = read<any>(KEY_TRACKERS);
  const next = all.filter(
    (t: any) =>
      !(
        String(t.packetId) === String(packetId) &&
        (t.id === idOrTitle || String(t.title || '').toLowerCase() === String(idOrTitle || '').toLowerCase())
      ),
  );
  write(KEY_TRACKERS, next);
  return true;
}

export async function reassignInvite(packetId: string, newInviteId: string): Promise<any> {
  const packets = read<any>(KEY_PACKETS);
  const answers = read<any>(KEY_ANSWERS);
  const trackers = read<any>(KEY_TRACKERS);
  const idx = packets.findIndex((p: any) => String(p.id) === String(packetId));
  if (idx < 0) throw new Error('packet not found');
  // drop any empty packet with the target invite id
  const existingIdx = packets.findIndex((p: any) => String(p.inviteId) === String(newInviteId));
  if (existingIdx >= 0 && existingIdx !== idx) {
    const targetId = packets[existingIdx].id;
    const hasKids =
      (answers || []).some((a: any) => String(a.packetId) === String(targetId)) ||
      (trackers || []).some((t: any) => String(t.packetId) === String(targetId));
    if (!hasKids) {
      packets.splice(existingIdx, 1);
    } else {
      throw new Error('Target invite already has a non-empty packet');
    }
  }
  packets[idx].inviteId = String(newInviteId);
  write(KEY_PACKETS, packets);
  return { id: String(packetId), invite_id: String(newInviteId) };
}

async function setPacketStatus(packetId: string, status: IntakePacket['status']): Promise<void> {
  const packets = read<IntakePacket>(KEY_PACKETS);
  const idx = packets.findIndex((p) => p.id === packetId);
  if (idx >= 0) {
    packets[idx] = { ...packets[idx], status, updatedAt: nowISO() };
    write(KEY_PACKETS, packets);
  }
}

export async function submitPacket(packetId: string): Promise<void> {
  await setPacketStatus(packetId, 'submitted');
}

export async function listAnswersForPacket(packetId: string): Promise<IntakeAnswer[]> {
  const answers = read<IntakeAnswer>(KEY_ANSWERS);
  return answers.filter((a) => String(a.packetId) === String(packetId));
}

export async function deleteAnswer(packetId: string, section: string, key: string): Promise<boolean> {
  const answers = read<IntakeAnswer>(KEY_ANSWERS);
  const next = answers.filter(
    (a) => !(String(a.packetId) === String(packetId) && a.section === (section as any) && a.key === key),
  );
  write(KEY_ANSWERS, next);
  return true;
}

export async function markMerged(packetId: string, clientId: string): Promise<IntakePacket> {
  const packets = read<IntakePacket>(KEY_PACKETS);
  const idx = packets.findIndex((p) => p.id === packetId);
  if (idx < 0) throw new Error('Packet not found');
  const existing: any = packets[idx];
  if (existing && existing.mergedAt) {
    return existing as IntakePacket;
  }
  const updated: IntakePacket = { ...existing, updatedAt: nowISO() } as IntakePacket;
  (updated as any).clientId = String(clientId);
  (updated as any).mergedAt = nowISO();
  packets[idx] = updated;
  write(KEY_PACKETS, packets);
  return updated;
}
